import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Person,
  Send,
  Lock,
  Security,
  CheckCircle,
  ArrowBack,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { paymentsAPI, transferPinAPI, usersAPI, newIdempotencyKey } from '../../services/api';
import { fetchTransactions } from '../../store/slices/transactionsSlice';

const QRPayPage: React.FC = () => {
  const idemKeyRef = useRef<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Parse recipient from URL
  const recipientData = useMemo(() => {
    try {
      const toParam = searchParams.get('to');
      if (!toParam) return null;
      const decoded = JSON.parse(atob(toParam));
      // Validate schema: only email and name fields allowed
      if (typeof decoded !== 'object' || decoded === null) return null;
      const validKeys = Object.keys(decoded).every(key => ['email', 'name'].includes(key));
      if (!validKeys) return null;
      if (decoded.email && typeof decoded.email !== 'string') return null;
      if (decoded.name && typeof decoded.name !== 'string') return null;
      return decoded;
    } catch {
      return null;
    }
  }, [searchParams]);

  // Steps: 0=amount, 1=confirm, 2=pin, 3=otp, 4=success
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resolvedRecipient, setResolvedRecipient] = useState<{ email?: string; name?: string } | null>(null);
  const [resolving, setResolving] = useState(false);

  // PIN/OTP state
  const [pinStatus, setPinStatus] = useState<{ isPinSet: boolean; isLocked: boolean } | null>(null);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [successReference, setSuccessReference] = useState('');
  const [showPin, setShowPin] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Store the full URL so we can redirect back after login (only relative URLs)
      const returnUrl = window.location.pathname + window.location.search;
      // Validate that the URL is relative (starts with / and doesn't contain protocol)
      if (returnUrl.startsWith('/') && !returnUrl.match(/^https?:\/\//)) {
        sessionStorage.setItem('returnAfterLogin', returnUrl);
      }
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Resolve recipient on mount
  useEffect(() => {
    if (!recipientData?.email || !isAuthenticated) return;
    const resolve = async () => {
      setResolving(true);
      try {
        const response = await usersAPI.resolvePublicUser({ email: recipientData.email });
        const data = response.data.data;
        setResolvedRecipient({
          email: data.email,
          name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
        });
      } catch {
        // Fall back to QR data
        setResolvedRecipient({ email: recipientData.email, name: recipientData.name || '' });
      }
      setResolving(false);
    };
    resolve();
  }, [recipientData, isAuthenticated]);

  // Fetch PIN status
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchPinStatus = async () => {
      try {
        const res = await transferPinAPI.getStatus();
        setPinStatus(res.data.data || res.data);
      } catch {
        setPinStatus({ isPinSet: false, isLocked: false });
      }
    };
    fetchPinStatus();
  }, [isAuthenticated]);

  if (!recipientData) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #EDF2FA 0%, #F4F7FC 50%, #E8F0F9 100%)' }}>
        <Card sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: { xs: '100%', sm: 400 }, mx: 2, textAlign: 'center', borderRadius: 4, boxShadow: '0 8px 40px rgba(9, 72, 109, 0.12)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', mb: 2 }}>Invalid Payment Link</Typography>
          <Typography sx={{ color: '#7F839C', mb: 3 }}>This QR code or payment link is invalid or has expired.</Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', borderRadius: 3 }}>
            Go to Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  // Check if sending to self
  const isSelf = user?.email?.toLowerCase() === recipientData.email?.toLowerCase();

  const recipientName = resolvedRecipient?.name || recipientData.name || 'Unknown';
  const recipientEmail = resolvedRecipient?.email || recipientData.email || '';
  const recipientInitials = recipientName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const stepLabels = ['Amount', 'Confirm', 'PIN', 'OTP', 'Done'];

  const handleNext = () => {
    setError(null);
    if (!amount || Number(amount) <= 0) { setError('Enter a valid amount'); return; }
    if (isSelf) { setError('You cannot send money to yourself'); return; }
    setStep(1);
  };

  const handleConfirm = () => { setStep(2); setPin(''); setNewPin(''); setConfirmNewPin(''); };

  const handlePinSubmit = async () => {
    setError(null);
    if (!pinStatus?.isPinSet) {
      if (newPin.length < 4) { setError('PIN must be at least 4 digits'); return; }
      if (newPin !== confirmNewPin) { setError('PINs do not match'); return; }
      setSubmitting(true);
      try {
        await transferPinAPI.setPin(newPin);
        setPinStatus({ isPinSet: true, isLocked: false });
        setPin(newPin);
        setOtpSending(true);
        await transferPinAPI.sendTransferOTP();
        setOtpSent(true);
        setStep(3);
      } catch (err: any) { setError(err.response?.data?.error?.message || 'Failed to set PIN'); }
      finally { setSubmitting(false); setOtpSending(false); }
    } else {
      if (pin.length < 4) { setError('Enter your transfer PIN'); return; }
      setSubmitting(true);
      try {
        await transferPinAPI.verifyPin(pin);
        setOtpSending(true);
        await transferPinAPI.sendTransferOTP();
        setOtpSent(true);
        setStep(3);
      } catch (err: any) { setError(err.response?.data?.error?.message || 'Invalid PIN'); }
      finally { setSubmitting(false); setOtpSending(false); }
    }
  };

  const handleTransfer = async () => {
    setError(null);
    if (!otp || otp.length < 6) { setError('Enter the 6-digit OTP sent to your email'); return; }
    setSubmitting(true);
    if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey();
    try {
      const response = await paymentsAPI.transfer({
        recipientEmail: recipientEmail,
        amount: Number(amount),
        currency: 'NGN',
        settlementCurrency: 'NGN',
        description: description || `QR payment to ${recipientName}`,
        pin: pinStatus?.isPinSet ? pin || newPin : newPin,
        otp,
      }, idemKeyRef.current);
      idemKeyRef.current = null;
      setSuccessReference(response.data.reference || response.data.data?.reference || '');
      setStep(4);
      dispatch(fetchTransactions());
    } catch (err: any) {
      // A definitive answer from the server ends this attempt; a network failure
      // keeps the key so a retry cannot charge twice.
      if (err.response) idemKeyRef.current = null;
      setError(err.response?.data?.error?.message || (err.response ? 'Transfer failed' : 'Network error. Please retry — your transfer will not be duplicated.'));
    }
    finally { setSubmitting(false); }
  };

  const handleResendOTP = async () => {
    setOtpSending(true);
    try {
      await transferPinAPI.sendTransferOTP();
      setOtpSent(true);
    } catch (err: any) { setError(err.response?.data?.error?.message || 'Failed to send OTP'); }
    finally { setOtpSending(false); }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #EDF2FA 0%, #F4F7FC 25%, #F8F8FC 50%, #F0F6FB 75%, #E8F0F9 100%)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      pt: { xs: 2, sm: 4, md: 8 },
      px: { xs: 1.5, sm: 2 },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed', top: 0, right: 0,
        width: '50vw', height: '50vh',
        background: 'radial-gradient(ellipse at 80% 20%, rgba(17, 193, 236, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      },
    }}>
      <Box sx={{ maxWidth: 480, width: '100%', position: 'relative', zIndex: 1 }}>
        {/* MalPay Branding */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src="https://mal-pay.com/images/logo22.png" alt="MalPay" sx={{ height: 36, mb: 1 }}
            onError={(e: any) => { e.target.style.display = 'none'; }} />
          <Typography sx={{
            fontWeight: 800, fontSize: '1.3rem',
            background: 'linear-gradient(135deg, #11C1EC, #09486D)',
            backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            MalPay
          </Typography>
        </Box>

        {/* Stepper */}
        {step < 4 && (
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3,
            '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 600 },
            '& .MuiStepIcon-root.Mui-active': { color: '#11C1EC' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#00C48C' },
          }}>
            {stepLabels.slice(0, 4).map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {/* Main Card */}
        <Card sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(9, 72, 109, 0.12)',
          border: 'none',
        }}>
          {/* Recipient header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', gap: 2,
            position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            {step > 0 && step < 4 && (
              <IconButton onClick={() => setStep(step - 1)} sx={{ color: 'white', mr: -0.5 }} size="small">
                <ArrowBack fontSize="small" />
              </IconButton>
            )}
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, color: 'white', fontSize: '1rem',
            }}>
              {step === 4 ? <CheckCircle sx={{ fontSize: 28 }} /> : resolving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : recipientInitials}
            </Box>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {step === 4 ? 'Transfer Complete' : 'Sending to'}
              </Typography>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                {recipientName}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                {recipientEmail}
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: 'white' }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            {/* Step 0: Amount */}
            {step === 0 && (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#202647', fontSize: '1rem', mb: 2 }}>
                  Enter payment details
                </Typography>
                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(null); }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Typography sx={{ fontWeight: 700, color: '#09486D' }}>₦</Typography></InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                  autoFocus
                />
                <TextField
                  label="Description (optional)"
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Payment for groceries"
                  sx={{ mb: 3 }}
                />
                {isSelf && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>You cannot send money to yourself.</Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNext}
                  disabled={!amount || Number(amount) <= 0 || isSelf}
                  sx={{
                    background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
                    borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1rem',
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  Continue
                </Button>
              </Box>
            )}

            {/* Step 1: Confirm */}
            {step === 1 && (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#202647', fontSize: '1rem', mb: 2 }}>
                  Confirm transfer
                </Typography>
                <Box sx={{ p: 2.5, borderRadius: 3, background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.06) 0%, rgba(9, 72, 109, 0.04) 100%)', border: '1px solid rgba(17, 193, 236, 0.1)', mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>Recipient</Typography>
                    <Typography sx={{ color: '#202647', fontWeight: 600, fontSize: '0.85rem' }}>{recipientName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>Email</Typography>
                    <Typography sx={{ color: '#202647', fontWeight: 600, fontSize: '0.85rem' }}>{recipientEmail}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: description ? 1.5 : 0 }}>
                    <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>Amount</Typography>
                    <Typography sx={{ color: '#09486D', fontWeight: 800, fontSize: '1.1rem' }}>₦{Number(amount).toLocaleString()}</Typography>
                  </Box>
                  {description && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>Description</Typography>
                      <Typography sx={{ color: '#202647', fontWeight: 500, fontSize: '0.85rem', maxWidth: '60%', textAlign: 'right' }}>{description}</Typography>
                    </Box>
                  )}
                </Box>
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2, fontSize: '0.8rem' }}>
                  Once sent, this transfer cannot be reversed.
                </Alert>
                <Button fullWidth variant="contained" onClick={handleConfirm}
                  sx={{ background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1rem' }}>
                  Confirm & Pay
                </Button>
              </Box>
            )}

            {/* Step 2: PIN */}
            {step === 2 && (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Lock sx={{ fontSize: 36, color: '#11C1EC', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#202647', fontSize: '1rem' }}>
                    {pinStatus?.isPinSet ? 'Enter Transfer PIN' : 'Set Your Transfer PIN'}
                  </Typography>
                  <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>
                    {pinStatus?.isPinSet ? 'Enter your PIN to authorize this payment' : 'Create a 4-6 digit PIN for secure transfers'}
                  </Typography>
                </Box>
                {pinStatus?.isPinSet ? (
                  <TextField
                    label="Transfer PIN"
                    type={showPin ? 'text' : 'password'}
                    fullWidth
                    value={pin}
                    onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    sx={{ mb: 2 }}
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPin(!showPin)} size="small">
                            {showPin ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <>
                    <TextField label="New PIN" type="password" fullWidth value={newPin}
                      onChange={(e) => { setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                      inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 2 }} autoFocus />
                    <TextField label="Confirm PIN" type="password" fullWidth value={confirmNewPin}
                      onChange={(e) => { setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                      inputProps={{ maxLength: 6, inputMode: 'numeric' }} sx={{ mb: 2 }} />
                  </>
                )}
                <Button fullWidth variant="contained" onClick={handlePinSubmit} disabled={submitting}
                  sx={{ background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', borderRadius: 3, py: 1.5, fontWeight: 700 }}>
                  {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
                </Button>
              </Box>
            )}

            {/* Step 3: OTP */}
            {step === 3 && (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Security sx={{ fontSize: 36, color: '#11C1EC', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#202647', fontSize: '1rem' }}>Verify OTP</Typography>
                  <Typography sx={{ color: '#7F839C', fontSize: '0.85rem' }}>
                    Enter the 6-digit code sent to your email
                  </Typography>
                </Box>
                <TextField
                  label="OTP Code"
                  fullWidth
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  sx={{ mb: 2 }}
                  autoFocus
                />
                <Button fullWidth variant="contained" onClick={handleTransfer} disabled={submitting || otp.length < 6}
                  sx={{ background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', borderRadius: 3, py: 1.5, fontWeight: 700, mb: 1 }}>
                  {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : `Pay ₦${Number(amount).toLocaleString()}`}
                </Button>
                <Button fullWidth variant="text" onClick={handleResendOTP} disabled={otpSending}
                  sx={{ color: '#11C1EC', fontWeight: 600, fontSize: '0.85rem' }}>
                  {otpSending ? 'Sending...' : 'Resend OTP'}
                </Button>
              </Box>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
                  background: 'linear-gradient(135deg, rgba(0, 196, 140, 0.15) 0%, rgba(0, 196, 140, 0.05) 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircle sx={{ fontSize: 40, color: '#00C48C' }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: '#202647', fontSize: '1.2rem', mb: 0.5 }}>
                  Payment Successful!
                </Typography>
                <Typography sx={{ color: '#09486D', fontWeight: 800, fontSize: '1.5rem', mb: 1 }}>
                  ₦{Number(amount).toLocaleString()}
                </Typography>
                <Typography sx={{ color: '#7F839C', fontSize: '0.85rem', mb: 2 }}>
                  sent to {recipientName}
                </Typography>
                {successReference && (
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F8F8FC', mb: 3 }}>
                    <Typography sx={{ color: '#7F839C', fontSize: '0.75rem' }}>Reference</Typography>
                    <Typography sx={{ color: '#202647', fontWeight: 600, fontSize: '0.8rem', wordBreak: 'break-all' }}>{successReference}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/dashboard')}
                    sx={{ borderRadius: 3, py: 1.2, fontWeight: 600, borderColor: '#11C1EC', color: '#09486D' }}>
                    Dashboard
                  </Button>
                  <Button fullWidth variant="contained" onClick={() => navigate('/transactions')}
                    sx={{ background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)', borderRadius: 3, py: 1.2, fontWeight: 600 }}>
                    Transactions
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
          <Typography sx={{ color: '#B0B0C0', fontSize: '0.75rem' }}>
            Secure payments powered by Paystack
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default QRPayPage;
